import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { parseSectionParam, SECTION_PARAM_KEY, SectionParam } from "./sectionParam";

/**
 * Failsafe for the one-shot `?section=` contract: if the drawer closes before
 * the target section ever mounted (e.g. the manifest query was still
 * suspending), the ref callback never ran and the param would leak into the
 * next drawer open. Call with the drawer's open state; on close it discards
 * any unconsumed section param.
 */
export function useClearSectionParamOnClose(open: boolean): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasParam = parseSectionParam(searchParams.get(SECTION_PARAM_KEY)) != null;
  useEffect(() => {
    if (open || !hasParam) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SECTION_PARAM_KEY);
        return next;
      },
      { replace: true }
    );
  }, [open, hasParam, setSearchParams]);
}

/**
 * Consumes the one-shot `?section=` deep-link (see sectionParam.ts): returns a
 * ref callback to attach to the section's wrapper. When the node mounts while
 * `?section=` matches, it scrolls the node into view and clears the param so
 * later drawer opens (row clicks preserve location.search) don't re-scroll.
 */
export function useScrollToSectionParam(section: SectionParam): (node: HTMLElement | null) => void {
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldScroll = parseSectionParam(searchParams.get(SECTION_PARAM_KEY)) === section;

  return useCallback(
    (node: HTMLElement | null) => {
      if (!node || !shouldScroll) return;
      node.scrollIntoView({ behavior: "smooth", block: "start" });
      // Defer past the commit phase: on initial mount, React Router's
      // useSearchParams drops (and warns about) updates issued before its layout
      // effect marks the router active — a synchronous call here would be
      // silently ignored and the param would never clear.
      queueMicrotask(() => {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete(SECTION_PARAM_KEY);
            return next;
          },
          { replace: true }
        );
      });
    },
    [shouldScroll, setSearchParams]
  );
}
